import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import HeaderNav from "../components/HeaderNav";
import FutureRoleDetail from "../components/future/FutureRoleDetail";
import CareerPyramid from "../components/future/CareerPyramid";
import SourcePanel from "../components/future/SourcePanel";
import { FitRing, FitPill } from "../components/future/FitVisuals";
import { Loader2, TrendingUp, ChevronRight, Compass, Layers, BookmarkCheck, ArrowUpRight } from "lucide-react";

const payLabel = (role) => (role.pay_min ? `₹${role.pay_min}${role.pay_max ? `-${role.pay_max}` : "+"} L` : "—");

const FutureJobsPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [edition, setEdition] = useState("ai_global");
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [resumeId, setResumeId] = useState("");
  const [resumes, setResumes] = useState([]);
  const [busy, setBusy] = useState(true);
  const [shortlist, setShortlist] = useState(null);

  const loadShortlist = useCallback(async (rid) => {
    try {
      const { data } = await api.get(`/future-jobs-shortlist${rid ? `?resume_id=${rid}` : ""}`);
      setShortlist(data);
    } catch { /* shortlist is additive - never block the page */ }
  }, []);

  const requestRef = React.useRef(0);
  const load = useCallback(async (key, rid) => {
    const token = ++requestRef.current;
    setBusy(true);
    try {
      const { data: payload } = await api.get(`/future-jobs?edition=${key}${rid ? `&resume_id=${rid}` : ""}`);
      if (token === requestRef.current) setData(payload);
    } catch (e) {
      if (token === requestRef.current) toast.error(e?.response?.data?.detail || "Could not load that list. Please retry.");
    } finally { if (token === requestRef.current) setBusy(false); }
  }, []);

  useEffect(() => {
    if (!loading && !user) { navigate("/", { replace: true }); return; }
    if (!user) return;
    (async () => {
      const [metaRes, resumeRes] = await Promise.all([api.get("/future-jobs/meta"), api.get("/resumes")]);
      setMeta(metaRes.data);
      setResumes(resumeRes.data || []);
      const first = (resumeRes.data || [])[0]?.resume_id || "";
      setResumeId(first);
      load("ai_global", first);
      loadShortlist(first);
    })().catch(() => setBusy(false));
  }, [loading, user, navigate, load, loadShortlist]);

  const openRole = async (roleId) => {
    try {
      const { data: payload } = await api.get(`/future-jobs/${roleId}${resumeId ? `?resume_id=${resumeId}` : ""}`);
      setDetail({ ...payload, fit_types: data?.fit_types });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not open that role. Please retry.");
    }
  };

  const changeResume = (rid) => { setResumeId(rid); setDetail(null); load(edition, rid); loadShortlist(rid); };
  const changeEdition = (key) => { setEdition(key); setDetail(null); load(key, resumeId); };

  const removeSaved = async (roleId) => {
    try {
      await api.delete(`/future-jobs/${roleId}/shortlist`);
      loadShortlist(resumeId);
      load(edition, resumeId);
      toast.success("Removed from your shortlist");
    } catch {
      toast.error("Could not update your shortlist");
    }
  };

  if (loading || (busy && !data)) {
    return <div className="min-h-screen bg-slate-50"><HeaderNav /><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div></div>;
  }

  const groups = data?.groups || [];
  const best = data?.best_fits || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <HeaderNav />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10" data-testid="future-jobs-page">
        {detail ? (
          <FutureRoleDetail detail={detail} resumeId={resumeId} onBack={() => setDetail(null)} onReload={() => load(edition, resumeId)}
            onShortlist={() => { loadShortlist(resumeId); load(edition, resumeId); }} />
        ) : (
          <>
            <header>
              <p className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-sky-700"><TrendingUp className="h-3.5 w-3.5" /> Future Jobs</p>
              <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{data?.edition?.label}</h1>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">{data?.edition?.headline}</p>
            </header>

            <div className="mt-5 flex flex-wrap items-end gap-3">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">List
                <select value={edition} onChange={(e) => changeEdition(e.target.value)} className="mt-1 block w-full min-w-[220px] rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900" data-testid="future-edition-select">
                  {(meta?.editions || []).map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </label>
              {resumes.length > 0 && (
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Score against
                  <select value={resumeId} onChange={(e) => changeResume(e.target.value)} className="mt-1 block w-full min-w-[220px] rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900" data-testid="future-resume-select">
                    {resumes.map((r) => <option key={r.resume_id} value={r.resume_id}>{r.filename}</option>)}
                  </select>
                </label>
              )}
            </div>

            {data?.edition?.stats?.length > 0 && (
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="future-stats">
                {data.edition.stats.map((s) => (
                  <div key={s.label} className="accent-bar card-soft p-4 pl-5" style={{ "--accent": "#0284C7" }}>
                    <p className="num-display text-xl text-slate-900">{s.value}</p>
                    <p className="mt-1 text-xs text-slate-700">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {best.length > 0 && (
              <div className="mt-5 accent-bar card-soft p-4 pl-5" style={{ "--accent": "#059669" }} data-testid="future-best-fits">
                <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">Best suited to you right now</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {best.map((b) => (
                    <button key={b.id} onClick={() => openRole(b.id)} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100" data-testid={`future-best-${b.id}`}>
                      {b.title} <span className="num-display">{b.score}</span> <ChevronRight className="h-3 w-3" />
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-slate-500">Scored against {data.scored_against || "your resume"}{data.preference ? ` · your stated preference: ${data.preference}` : ""}. {meta?.scoring?.note}</p>
                {data.best_fits_note && <p className="mt-1 text-[11px] text-slate-600" data-testid="future-best-fits-note">{data.best_fits_note}</p>}
              </div>
            )}

            {shortlist?.items?.length > 0 && (
              <section className="mt-5 card-soft p-4" data-testid="future-shortlist">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg"><BookmarkCheck className="h-4 w-4 text-sky-600" /> Your shortlist</h2>
                  <span className="num-display text-[11px] text-slate-400">{shortlist.items.length} of {shortlist.max}</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">{shortlist.note}</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  {shortlist.items.map((item) => (
                    <div key={item.role_id} className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`shortlist-item-${item.role_id}`}>
                      <button onClick={() => openRole(item.role_id)} className="text-left text-sm font-semibold text-slate-900 hover:text-sky-700">{item.title}</button>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {item.fit && <span className="num-display text-[11px] text-slate-600">{item.fit.score} of 100</span>}
                        {typeof item.progress === "number" && item.progress !== 0 && (
                          <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${item.progress > 0 ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid={`shortlist-progress-${item.role_id}`}>
                            {item.progress > 0 ? <ArrowUpRight className="h-3 w-3" /> : null}{item.progress > 0 ? `+${item.progress}` : item.progress} since saved
                          </span>
                        )}
                        {item.has_plan && <span className="rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-700">Plan ready</span>}
                      </div>
                      <button onClick={() => removeSaved(item.role_id)} className="mt-2 text-[11px] text-slate-400 hover:text-rose-600" data-testid={`shortlist-remove-${item.role_id}`}>Remove</button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {groups.map((group) => {
              const roles = (data?.roles || []).filter((r) => r.group === group.key);
              if (!roles.length) return null;
              return (
                <section key={group.key} className="mt-7" data-testid={`future-group-${group.key}`}>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <h2 className="text-base font-bold text-slate-900 md:text-lg" style={{ color: group.accent }}>{group.label}</h2>
                    <span className="num-display text-[11px] text-slate-400">{group.range}</span>
                    <p className="text-xs text-slate-500">{group.blurb}</p>
                  </div>
                  <div className="mt-3 grid auto-rows-fr gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {roles.map((role, i) => (
                      <button key={role.id} onClick={() => openRole(role.id)}
                        className={`accent-bar card-soft card-soft-hover p-4 pl-5 text-left ${i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}`}
                        style={{ "--accent": group.accent }} data-testid={`future-role-tile-${role.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="num-display text-[11px] text-slate-400">{String(role.rank).padStart(2, "0")}</p>
                            <p className="mt-0.5 text-sm font-bold leading-snug text-slate-900">{role.title}</p>
                          </div>
                          {role.fit ? <FitRing score={role.fit.score} verdict={role.fit.verdict} belowLevel={role.fit.below_level} size={58} testId={`future-tile-ring-${role.id}`} /> : null}
                        </div>
                        <p className="mt-2 text-xs text-slate-600">{role.what_it_does}</p>
                        {role.fit?.below_level && (
                          <p className="mt-2 text-[11px] text-slate-500" data-testid={`future-tile-below-${role.id}`}>{role.fit.level_note}</p>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-1.5">
                          {role.fit && <FitPill fit={role.fit} testId={`future-tile-pill-${role.id}`} />}
                          {(data?.shortlisted || []).includes(role.id) && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700"><BookmarkCheck className="h-3 w-3" /> Shortlisted</span>
                          )}
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-600">{(data?.fit_types || {})[role.fit_type] ? role.fit_type.toUpperCase() : role.fit_type}</span>
                          <span className="num-display rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[10px] text-teal-700">{payLabel(role)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}

            <CareerPyramid resumeId={resumeId} />

            {(data?.edition?.tips || data?.edition?.trends) && (
              <section className="mt-8 grid gap-3 lg:grid-cols-3" data-testid="future-playbook">
                {data.edition.tips?.find?.length > 0 && (
                  <div className="accent-bar card-soft p-4 pl-5" style={{ "--accent": "#0284C7" }} data-testid="future-tips-find">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-sky-700">How to find these jobs on any site</p>
                    <ul className="mt-2 space-y-1.5">
                      {data.edition.tips.find.map((tip, i) => (
                        <li key={i} className="flex gap-2 text-xs text-slate-700"><span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-sky-300" />{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.edition.tips?.before_apply?.length > 0 && (
                  <div className="accent-bar card-soft p-4 pl-5" style={{ "--accent": "#D97706" }} data-testid="future-tips-before">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-amber-700">Before you apply</p>
                    <ul className="mt-2 space-y-1.5">
                      {data.edition.tips.before_apply.map((tip, i) => (
                        <li key={i} className="flex gap-2 text-xs text-slate-700"><span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" />{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.edition.trends?.length > 0 && (
                  <div className="accent-bar card-soft p-4 pl-5" style={{ "--accent": "#0D9488" }} data-testid="future-trends">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-teal-700">Where hiring is growing</p>
                    <div className="mt-2 space-y-3">
                      {data.edition.trends.map((block) => (
                        <div key={block.group}>
                          <p className="text-[10px] uppercase tracking-wider text-slate-400">{block.group}</p>
                          <ul className="mt-1 space-y-1">
                            {block.items.map((row) => (
                              <li key={row.label} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs text-slate-700">
                                <span className="min-w-0 break-words">{row.label}</span><span className="num-display break-words text-slate-900">{row.value}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <p className="inline-flex items-start gap-2 text-[11px] text-slate-500">
                <Compass className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {data?.edition?.grouped_note}
              </p>
              <SourcePanel testId="future-page-sources" items={[
                ...(data?.edition?.stats || []).map((s) => `${s.value} — ${s.label}: ${s.source}`),
                data?.edition?.pay_note, data?.edition?.source_note]} />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default FutureJobsPage;
